import { useEffect, useState } from "react";
import { AlertCircle, Check, Copy, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useGenerateCopy } from "@/lib/hooks/useAiCopy";

export interface CopyGeneratorDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly productUrl: string;
  readonly affiliateUrl: string;
  readonly marketplaceLabel: string;
}

interface EditableCopy {
  titulo: string;
  legenda: string;
  hashtags: string;
  cta: string;
  mensagemCompleta: string;
}

const EMPTY_COPY: EditableCopy = { titulo: "", legenda: "", hashtags: "", cta: "", mensagemCompleta: "" };

/** Sheet lateral: gera copy de afiliado via IA e permite editar/copiar antes
 *  de postar. Aciona a geracao automaticamente ao abrir. */
export function CopyGeneratorDrawer({
  open,
  onClose,
  productUrl,
  affiliateUrl,
  marketplaceLabel,
}: CopyGeneratorDrawerProps): JSX.Element | null {
  const { generateCopy, loading, error } = useGenerateCopy();
  const [copy, setCopy] = useState<EditableCopy>(EMPTY_COPY);
  const [hasResult, setHasResult] = useState(false);
  const [copied, setCopied] = useState(false);

  const runGeneration = async (): Promise<void> => {
    setCopied(false);
    const result = await generateCopy({ productUrl, affiliateUrl, marketplaceLabel });
    if (!result) {
      setHasResult(false);
      return;
    }
    setCopy({
      titulo: result.titulo,
      legenda: result.legenda,
      hashtags: result.hashtags.join(", "),
      cta: result.cta,
      mensagemCompleta: result.mensagemCompleta,
    });
    setHasResult(true);
  };

  useEffect(() => {
    if (open) {
      setCopy(EMPTY_COPY);
      setHasResult(false);
      void runGeneration();
    }
  }, [open, productUrl, affiliateUrl]);

  if (!open) {
    return null;
  }

  const handleCopyMessage = async (): Promise<void> => {
    await navigator.clipboard.writeText(copy.mensagemCompleta);
    setCopied(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <aside className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-border bg-card shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <Sparkles size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Gerar copy com IA</h2>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-5 p-5">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={15} className="animate-spin" />
              Gerando copy persuasiva com Gemini...
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && hasResult && (
            <>
              <div className="space-y-2">
                <Label htmlFor="copy-titulo" variant="field">
                  Título
                </Label>
                <textarea
                  id="copy-titulo"
                  value={copy.titulo}
                  onChange={(event) => setCopy((prev) => ({ ...prev, titulo: event.target.value }))}
                  rows={2}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copy-legenda" variant="field">
                  Legenda
                </Label>
                <textarea
                  id="copy-legenda"
                  value={copy.legenda}
                  onChange={(event) => setCopy((prev) => ({ ...prev, legenda: event.target.value }))}
                  rows={4}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copy-hashtags" variant="field">
                  Hashtags
                </Label>
                <textarea
                  id="copy-hashtags"
                  value={copy.hashtags}
                  onChange={(event) => setCopy((prev) => ({ ...prev, hashtags: event.target.value }))}
                  rows={2}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copy-cta" variant="field">
                  Chamada para ação
                </Label>
                <textarea
                  id="copy-cta"
                  value={copy.cta}
                  onChange={(event) => setCopy((prev) => ({ ...prev, cta: event.target.value }))}
                  rows={1}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copy-mensagem" variant="field">
                  Mensagem completa (o que sera copiado)
                </Label>
                <textarea
                  id="copy-mensagem"
                  value={copy.mensagemCompleta}
                  onChange={(event) => setCopy((prev) => ({ ...prev, mensagemCompleta: event.target.value }))}
                  rows={6}
                  className="w-full resize-none rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-overlay)/0.6)] px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-border bg-[hsl(var(--surface-raised)/0.3)] p-4">
          <Button type="button" variant="outline" onClick={() => void runGeneration()} disabled={loading}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Regenerar
          </Button>
          <Button
            type="button"
            variant="default"
            className="flex-1"
            onClick={() => void handleCopyMessage()}
            disabled={loading || !hasResult}
          >
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? "Copiado!" : "Copiar mensagem completa"}
          </Button>
        </div>
      </aside>
    </div>
  );
}
